import type {
  AdminResource,
  ResourceOperatingHour,
  ResourceWeekday,
} from '@mds/shared/types/resource';

/**
 * The rooms' weekly opening hours, as the boards that plot them need to ask.
 *
 * Every window comes off the resource the API returned — `operatingHours` is
 * part of the resource contract, and the availability read carries the same
 * figures per day as `openFromMinute` / `openToMinute`. Nothing here assumes a
 * house opening time: the hours are editable, so a board that hard-coded them
 * would quietly go wrong the day someone changes one.
 */

/** `Date.getDay()` is Sunday-first; the contract's weekday key is not. */
const SUNDAY_FIRST: readonly ResourceWeekday[] = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

/** The weekday key a local date falls on. */
export const weekdayOf = (date: Date): ResourceWeekday => SUNDAY_FIRST[date.getDay()];

/** A room's window on one weekday, or `null` when it is shut. */
export function openWindow(
  resource: Pick<AdminResource, 'operatingHours'>,
  weekday: ResourceWeekday,
): { openMinute: number; closeMinute: number } | null {
  const entry: ResourceOperatingHour | undefined = resource.operatingHours.find(
    (h) => h.weekday === weekday,
  );
  if (!entry || entry.isClosed || entry.openMinute === null || entry.closeMinute === null) {
    return null;
  }
  return { openMinute: entry.openMinute, closeMinute: entry.closeMinute };
}

/**
 * The whole-hour marks a grid needs to cover every given room on a weekday.
 *
 * Widest window wins, floored and ceiled to the hour so a room opening at
 * 09:30 still gets a 09:00 row to sit in. Empty when every room is shut, which
 * is the caller's cue to say so rather than draw an empty board.
 */
export function hourMarks(
  resources: ReadonlyArray<Pick<AdminResource, 'operatingHours'>>,
  weekday: ResourceWeekday,
): number[] {
  const windows = resources.flatMap((r) => {
    const w = openWindow(r, weekday);
    return w ? [w] : [];
  });
  if (windows.length === 0) return [];

  const from = Math.floor(Math.min(...windows.map((w) => w.openMinute)) / 60);
  // A window closing at 22:00 has nothing to show in a 22:00 row, so the last
  // mark is the hour the final minute falls in.
  const to = Math.ceil(Math.max(...windows.map((w) => w.closeMinute)) / 60);
  return Array.from({ length: Math.max(to - from, 0) }, (_, i) => from + i);
}

/** True when an hour mark falls inside the room's window for that weekday. */
export function isOpenAt(
  resource: Pick<AdminResource, 'operatingHours'>,
  weekday: ResourceWeekday,
  hour: number,
): boolean {
  const w = openWindow(resource, weekday);
  if (!w) return false;
  const minute = hour * 60;
  return minute >= w.openMinute && minute < w.closeMinute;
}
